import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";
import { reportClientError } from "../lib/observability";
import { extractErrorMessage } from "../lib/route-error";

export { extractErrorMessage };

// Root-level errorComponent for TanStack Router. Without one, any render
// throw inside a route surfaces the library default ("Something went
// wrong!") with no error text, no retry, no telemetry, and the actual
// error message is swallowed by the route CatchBoundary before it can
// bubble to `window.onerror`, so we never learn what broke.
//
// This component (a) reports the error to /api/client-error via
// reportClientError so it lands in the Observability tab, (b) renders a
// graceful retry UI that the user can dismiss with a tap, and (c)
// re-invalidates the router so React re-mounts the tree cleanly on retry.
//
// Wired via `createRootRoute({ errorComponent: RouteErrorFallback })`;
// every child route inherits unless it defines its own errorComponent.
export type RouteErrorFallbackProps = {
  error: unknown;
  reset?: () => void;
};

export function RouteErrorFallback({ error, reset }: RouteErrorFallbackProps) {
  const router = useRouter();
  const message = extractErrorMessage(error);
  const stack = error instanceof Error ? error.stack : undefined;

  useEffect(() => {
    reportClientError(message, stack);
  }, [message, stack]);

  const handleRetry = () => {
    if (reset) reset();
    router.invalidate();
  };

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center gap-4 px-4 py-12 text-center">
      <div className="rounded-full bg-[var(--surface-2)] p-4 text-[var(--muted)]">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="h-8 w-8"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
          />
        </svg>
      </div>
      <h2 className="text-lg font-semibold text-[var(--foreground)]">
        Couldn't load this page
      </h2>
      <p className="text-sm text-[var(--muted)]">
        {message}
      </p>
      <button
        type="button"
        onClick={handleRetry}
        className="rounded-full border border-[var(--border-default)] bg-[var(--surface)] px-4 py-2 text-sm font-medium text-[var(--foreground)] transition hover:bg-[var(--surface-2)]"
      >
        Try again
      </button>
    </div>
  );
}
